import type { Injector, Signal } from '@angular/core'
import { computed, Injectable, inject } from '@angular/core'
import type { TaskStatus } from '@awork/features/task/models/task-status.model'
import { TaskStatusStore } from '@awork/features/task/state/task-status.store'
import type { Observable } from 'rxjs'
import { map } from 'rxjs'
import { EntitySignalQuery } from '@awork/core/state/signal-store/entitySignalQuery'

const sortStatuses = (a: TaskStatus, b: TaskStatus) => a.order - b.order

@Injectable({ providedIn: 'root' })
export class TaskStatusQuery extends EntitySignalQuery<TaskStatus> {
  protected store: TaskStatusStore

  constructor() {
    const store = inject(TaskStatusStore)

    super(store)

    this.store = store
  }

  /**
   * Gets the task status by id
   * @param {string} taskStatusId
   * @returns {Signal<TaskStatus | null>}
   */
  queryTaskStatus(taskStatusId: string): Signal<TaskStatus | null> {
    return computed(() => this.getMappedTaskStatus(taskStatusId))
  }

  /**
   * Selects the task statuses of a project
   * @param {Injector} injector Angular injector
   * @param {string} projectId
   * @returns {Observable<TaskStatus[]>}
   * @deprecated
   */
  selectProjectStatuses(injector: Injector, projectId: string): Observable<TaskStatus[]> {
    return this.selectAll(injector, {
      filterBy: taskStatus => taskStatus.projectId === projectId,
      sortBy: sortStatuses
    }).pipe(map(taskStatuses => this.mapEntities(taskStatuses)))
  }

  /**
   * Gets the task statuses of a project
   * @param {string} projectId
   * @returns {TaskStatus[]}
   */
  getProjectStatuses(projectId: string): TaskStatus[] {
    const taskStatuses = this.getAll({
      filterBy: taskStatus => taskStatus.projectId === projectId,
      sortBy: sortStatuses
    })

    return this.mapEntities(taskStatuses)
  }

  /**
   * Gets the task statuses of a project
   * @param {string} projectId
   * @returns {Signal<TaskStatus[]>}
   */
  queryProjectStatuses(projectId: string): Signal<TaskStatus[]> {
    return computed(() => this.getMappedProjectStatuses(projectId))
  }

  /**
   * Gets all the project task statuses
   * @returns {TaskStatus[]}
   */
  getProjectsStatuses(): TaskStatus[] {
    const taskStatuses = this.getAll({
      filterBy: taskStatus => !!taskStatus.projectId,
      sortBy: sortStatuses
    })

    return this.mapEntities(taskStatuses)
  }

  /**
   * Selects the task statuses of a project template
   * @param {Injector} injector Angular injector
   * @param {string} projectTemplateId
   * @returns {Observable<TaskStatus[]>}
   * @deprecated
   */
  selectProjectTemplateStatuses(injector: Injector, projectTemplateId: string): Observable<TaskStatus[]> {
    return this.selectAll(injector, {
      filterBy: taskStatus => taskStatus.projectTemplateId === projectTemplateId,
      sortBy: sortStatuses
    }).pipe(map(taskStatuses => this.mapEntities(taskStatuses)))
  }

  /**
   * Gets the task statuses of a project template
   * @param {string} projectTemplateId
   * @returns {Signal<TaskStatus[]>}
   */
  queryProjectTemplateStatuses(projectTemplateId: string): Signal<TaskStatus[]> {
    return computed(() => this.getMappedProjectTemplateStatuses(projectTemplateId))
  }

  /**
   * Gets the task statuses of a workflow
   * @param {string} workflowId
   * @returns {Signal<TaskStatus[]>}
   */
  queryWorkflowStatuses(workflowId: string): Signal<TaskStatus[]> {
    return computed(() => {
      const taskStatuses = this.queryAll({
        filterBy: taskStatus => taskStatus.workflowId === workflowId,
        sortBy: sortStatuses
      })

      return this.mapEntities(taskStatuses())
    })
  }

  /**
   * Gets the task statuses of a project template
   * @param {string} projectTemplateId
   * @returns {TaskStatus[]}
   */
  getProjectTemplateStatuses(projectTemplateId: string): TaskStatus[] {
    const taskStatuses = this.getAll({
      filterBy: taskStatus => taskStatus.projectTemplateId === projectTemplateId,
      sortBy: sortStatuses
    })

    return this.mapEntities(taskStatuses)
  }

  /**
   * Gets the task statuses for private tasks
   * @returns {Signal<TaskStatus[]>}
   */
  queryPrivateStatuses(): Signal<TaskStatus[]> {
    return computed(() => this.getMappedPrivateStatuses())
  }

  /**
   * Select the task statuses for private tasks
   * @param {Injector} injector Angular injector
   * @returns {Observable<TaskStatus[]>}
   * @deprecated
   */
  selectPrivateStatuses(injector: Injector): Observable<TaskStatus[]> {
    return this.selectAll(injector, {
      filterBy: taskStatus => !taskStatus.projectTemplateId && !taskStatus.projectId && !taskStatus.workflowId,
      sortBy: sortStatuses
    }).pipe(map(taskStatuses => this.mapEntities(taskStatuses)))
  }

  /**
   * Gets the task statuses for private tasks
   * @returns {TaskStatus[]}
   */
  getPrivateStatuses(): TaskStatus[] {
    const taskStatuses = this.getAll({
      filterBy: taskStatus => !taskStatus.projectTemplateId && !taskStatus.projectId && !taskStatus.workflowId,
      sortBy: sortStatuses
    })

    return this.mapEntities(taskStatuses)
  }

  /**
   * Returns the mapped task status for the given id.
   * @param {string} taskStatusId
   * @returns {TaskStatus | null}
   * @withSignal {TaskStatus | undefined} queryEntity(taskStatusId)
   */
  private getMappedTaskStatus(taskStatusId: string): TaskStatus | null {
    const taskStatus = this.queryEntity(taskStatusId)

    return taskStatus()
  }

  /**
   * Returns the mapped task statuses for a project.
   * @param {string} projectId
   * @returns {TaskStatus[]}
   * @withSignal {TaskStatus[]} queryAll({ filterBy, sortBy })
   */
  private getMappedProjectStatuses(projectId: string): TaskStatus[] {
    const taskStatuses = this.queryAll({
      filterBy: taskStatus => taskStatus.projectId === projectId,
      sortBy: sortStatuses
    })

    return taskStatuses()
  }

  /**
   * Returns the mapped task statuses for a project template.
   * @param {string} projectTemplateId
   * @returns {TaskStatus[]}
   * @withSignal {TaskStatus[]} queryAll({ filterBy, sortBy })
   */
  private getMappedProjectTemplateStatuses(projectTemplateId: string): TaskStatus[] {
    const taskStatuses = this.queryAll({
      filterBy: taskStatus => taskStatus.projectTemplateId === projectTemplateId,
      sortBy: sortStatuses
    })

    return taskStatuses()
  }

  /**
   * Returns the mapped task statuses for private tasks.
   * @returns {TaskStatus[]}
   * @withSignal {TaskStatus[]} queryAll({ filterBy, sortBy })
   */
  private getMappedPrivateStatuses(): TaskStatus[] {
    const taskStatuses = this.queryAll({
      filterBy: taskStatus => !taskStatus.projectTemplateId && !taskStatus.projectId && !taskStatus.workflowId,
      sortBy: sortStatuses
    })

    return taskStatuses()
  }
}
